import React, { useEffect, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common/Bits";
import { Save, Upload, FileText, Trash2, Download, Eye, Sparkles, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useAIOutputs } from "@/hooks/useAIOutputs";
import AICategoryPanel from "@/components/ai/AICategoryPanel";

function Field({ label, value, onChange, ph, textarea, rows = 3, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{label}</label>
      {textarea ? (
        <textarea
          data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
          placeholder={ph} rows={rows}
          className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)] resize-none"
        />
      ) : (
        <input
          data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
          placeholder={ph}
          className="mt-1.5 w-full bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
        />
      )}
    </div>
  );
}

export default function CaseOverview() {
  const { t, currentCase, loadCases } = useApp();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [shareEmail, setShareEmail] = useState("");
  const [shareRole, setShareRole] = useState("viewer");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // doc object
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const fileRef = useRef(null);
  const { outputs: aiOutputs, addOutput: addAIOutput } = useAIOutputs(currentCase?.id);

  useEffect(() => {
    if (!currentCase) return;
    setForm({ ...currentCase, charges: (currentCase.charges || []).join(", ") });
    setMembers(currentCase.members || []);
    api.get(`/cases/${currentCase.id}/documents`).then(r => setDocs(r.data)).catch(()=>{});
  }, [currentCase]);

  const isOwner = user && currentCase && currentCase.owner_id === user.id;

  const share = async () => {
    if (!shareEmail) return;
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/share`, { email: shareEmail, role: shareRole });
      setMembers(m => [...m.filter(x => x.user_id !== data.member.user_id), data.member]);
      setShareEmail("");
      toast.success(`Κοινοποιήθηκε στον/στην ${data.member.email}`);
      await loadCases();
    } catch (e) { toast.error(formatError(e)); }
  };

  const unshare = async (memberId) => {
    try {
      await api.delete(`/cases/${currentCase.id}/share/${memberId}`);
      setMembers(m => m.filter(x => x.user_id !== memberId));
      await loadCases();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!form) return null;

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, charges: form.charges.split(",").map(s => s.trim()).filter(Boolean) };
      await api.patch(`/cases/${currentCase.id}`, payload);
      await loadCases();
      toast.success(t("saved"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("category", "case_overview");
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(d => [data, ...d]);
      toast.success(`📎 ${file.name}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const delDoc = async (id) => {
    await api.delete(`/cases/${currentCase.id}/documents/${id}`);
    setDocs(d => d.filter(x => x.id !== id));
    toast.success(t("deleted"));
  };

  const ingest = async (id) => {
    toast.loading("Επεξεργασία (OCR + chunking)…", { id: "ingest" });
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/documents/${id}/ingest`);
      toast.success(`Δημιουργήθηκαν ${data.chunks_created} chunks (${data.text_length} χαρακτήρες)`, { id: "ingest" });
      setDocs(d => d.map(x => x.id === id ? { ...x, chunks_count: data.chunks_created, ingested: true } : x));
    } catch (e) { toast.error(formatError(e), { id: "ingest" }); }
  };

  const semanticSearch = async () => {
    if (!searchQ || searchQ.length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.post(`/cases/${currentCase.id}/semantic-search`, { q: searchQ });
      setSearchResults(data.results || []);
      if ((data.results || []).length === 0) toast.message("Δεν βρέθηκαν αποτελέσματα");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSearching(false); }
  };

  return (
    <div data-testid="case-overview-page">
      <PageHeader
        title={t("case_overview")}
        subtitle="Συμπληρώστε όλα τα δομικά στοιχεία της υπόθεσης."
        action={
          <button data-testid="case-save-btn" onClick={save} disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px] disabled:opacity-60">
            <Save className="size-4"/>{saving ? t("loading") : t("save")}
          </button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 surface-card p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field testid="co-title" label="Τίτλος" value={form.title} onChange={v => update("title", v)}/>
            <Field testid="co-case-number" label="Αρ. Δικογραφίας" value={form.case_number} onChange={v => update("case_number", v)}/>
            <Field testid="co-client" label="Εντολέας / Κατηγορούμενος" value={form.client_name} onChange={v => update("client_name", v)}/>
            <Field testid="co-court" label="Δικαστήριο" value={form.court} onChange={v => update("court", v)}/>
            <Field testid="co-prosecutor" label="Εισαγγελική αρχή" value={form.prosecutor} onChange={v => update("prosecutor", v)}/>
            <Field testid="co-opposing" label="Αντίδικοι / Παθόντες" value={form.opposing_parties} onChange={v => update("opposing_parties", v)}/>
            <Field testid="co-stage" label="Στάδιο" value={form.stage} onChange={v => update("stage", v)}/>
            <Field testid="co-next-hearing" label="Επόμενη δικάσιμος" value={(form.next_hearing_date || "").slice(0,10)} onChange={v => update("next_hearing_date", v ? new Date(v).toISOString() : "")}/>
          </div>
          <Field testid="co-charges" label="Κατηγορίες (διαχωρισμός με κόμμα)" value={form.charges} onChange={v => update("charges", v)}/>
          <Field testid="co-legal-issues" label="Νομικά ζητήματα" value={form.legal_issues} onChange={v => update("legal_issues", v)} textarea rows={3}/>
          <Field testid="co-factual" label="Σύνοψη πραγματικών" value={form.factual_summary} onChange={v => update("factual_summary", v)} textarea rows={5}/>
          <Field testid="co-defense-pos" label="Θέση υπεράσπισης" value={form.defense_position} onChange={v => update("defense_position", v)} textarea rows={3}/>
          <Field testid="co-risks" label="Κύριοι κίνδυνοι" value={form.main_risks} onChange={v => update("main_risks", v)} textarea rows={3}/>
          <Field testid="co-strategic" label="Στρατηγικές σημειώσεις" value={form.strategic_notes} onChange={v => update("strategic_notes", v)} textarea rows={3}/>
        </div>

        <div className="space-y-4">
          <div className="surface-card p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold text-[13.5px]">Έγγραφα Υπόθεσης</h3>
              <StatusBadge tone="gold">{docs.length}</StatusBadge>
            </div>
            <div
              data-testid="upload-dropzone"
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border/60 rounded-lg p-6 text-center cursor-pointer hover:border-[hsl(var(--gold)/0.5)] transition-colors"
            >
              <Upload className="size-6 mx-auto mb-2 text-muted-foreground"/>
              <div className="text-[12.5px] font-medium">Drag & drop ή κάντε κλικ</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">PDF, DOCX, εικόνες, audio, video</div>
              <input ref={fileRef} type="file" hidden onChange={e => upload(e.target.files?.[0])}/>
            </div>
            <div className="mt-3 space-y-1.5">
              {docs.map(d => (
                <div key={d.id} className="flex items-center gap-2 p-2 rounded border border-border/40 hover:border-[hsl(var(--gold)/0.3)] transition-colors text-[12px]">
                  <FileText className="size-3.5 gold-text"/>
                  <span className="truncate flex-1 font-mono">{d.filename}</span>
                  {d.chunks_count ? <span className="text-[10px] gold-text font-mono">{d.chunks_count}c</span> : null}
                  <button onClick={() => setPreview(d)} title="Preview" data-testid={`doc-preview-${d.id}`}
                    className="text-muted-foreground hover:gold-text">
                    <Eye className="size-3.5"/>
                  </button>
                  <button onClick={() => ingest(d.id)} title="OCR + chunk" data-testid={`doc-ingest-${d.id}`}
                    className="text-muted-foreground hover:gold-text">
                    <Sparkles className="size-3.5"/>
                  </button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.id}/documents/${d.id}/download`} target="_blank" rel="noreferrer"
                     className="text-muted-foreground hover:gold-text" data-testid={`doc-download-${d.id}`}>
                    <Download className="size-3.5"/>
                  </a>
                  <button onClick={() => delDoc(d.id)} className="text-muted-foreground hover:text-red-400" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="size-3.5"/>
                  </button>
                </div>
              ))}
              {docs.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-2">{t("empty_state")}</div>}
            </div>
          </div>

          <div className="surface-card p-5" data-testid="semantic-search-panel">
            <h3 className="font-heading font-semibold text-[13.5px] mb-2 flex items-center gap-2">
              <Search className="size-4 gold-text"/>Semantic Search
            </h3>
            <p className="text-[11.5px] text-muted-foreground mb-3">Αναζήτηση στο πλήρες κείμενο όλων των εγγράφων (RAG). Πρώτα κάντε ingest στα έγγραφα.</p>
            <div className="flex gap-2 mb-3">
              <input
                data-testid="semantic-search-input"
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                onKeyDown={e => e.key === "Enter" && semanticSearch()}
                placeholder="π.χ. ψευδής δήλωση παθόντος αλκοόλ"
                className="flex-1 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"
              />
              <button data-testid="semantic-search-btn" onClick={semanticSearch} disabled={searching}
                className="px-3 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[12px] disabled:opacity-60">
                {searching ? "…" : "Search"}
              </button>
            </div>
            <div className="space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin">
              {searchResults.map((r, i) => (
                <div key={i} className="p-2.5 rounded border border-border/50 hover:border-[hsl(var(--gold)/0.3)] cursor-default" data-testid={`semantic-result-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono gold-text truncate">{r.citation}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{(r.score * 100).toFixed(0)}%</span>
                  </div>
                  <p className="text-[12px] leading-relaxed">{r.snippet}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="surface-card p-5">
            <h3 className="font-heading font-semibold text-[13.5px] mb-3">Δείκτες</h3>
            <Field testid="co-risk-score" label="Risk Score (0-100)" value={form.risk_score} onChange={v => update("risk_score", parseInt(v) || 0)}/>
            <div className="h-3"/>
            <Field testid="co-readiness" label="Defense Readiness (0-100)" value={form.defense_readiness} onChange={v => update("defense_readiness", parseInt(v) || 0)}/>
          </div>

          <div className="surface-card p-5" data-testid="share-panel">
            <h3 className="font-heading font-semibold text-[13.5px] mb-2 flex items-center gap-2">
              <Users className="size-4 gold-text"/>Κοινοποίηση υπόθεσης
            </h3>
            {isOwner ? (
              <>
                <p className="text-[11.5px] text-muted-foreground mb-3">Δώστε πρόσβαση σε συνεργάτη του γραφείου (πρέπει να έχει λογαριασμό).</p>
                <div className="flex gap-2 mb-3">
                  <input data-testid="share-email-input" value={shareEmail}
                    onChange={e => setShareEmail(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && share()}
                    placeholder="email συνεργάτη"
                    className="flex-1 min-w-0 bg-muted/30 border border-border/80 rounded-md px-3 py-2 text-[12.5px] outline-none focus:border-[hsl(var(--gold)/0.5)]"/>
                  <select value={shareRole} onChange={e => setShareRole(e.target.value)} data-testid="share-role-select"
                    className="bg-muted/30 border border-border/80 rounded-md px-2 py-2 text-[12px]">
                    <option value="viewer">Ανάγνωση</option>
                    <option value="editor">Επεξεργασία</option>
                  </select>
                  <button data-testid="share-btn" onClick={share}
                    className="px-3 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[12px]">+</button>
                </div>
              </>
            ) : (
              <p className="text-[11.5px] text-muted-foreground mb-3">Η υπόθεση σάς έχει κοινοποιηθεί από τον κάτοχό της.</p>
            )}
            <div className="space-y-1.5">
              {members.map(m => (
                <div key={m.user_id} className="flex items-center gap-2 p-2 rounded border border-border/40 text-[12px]" data-testid={`share-member-${m.user_id}`}>
                  <span className="truncate flex-1">{m.name || m.email}</span>
                  <StatusBadge tone="gold">{m.role === "editor" ? "Επεξεργασία" : "Ανάγνωση"}</StatusBadge>
                  {isOwner && (
                    <button onClick={() => unshare(m.user_id)} className="text-muted-foreground hover:text-red-400" data-testid={`share-remove-${m.user_id}`}>
                      <Trash2 className="size-3.5"/>
                    </button>
                  )}
                </div>
              ))}
              {members.length === 0 && <div className="text-[11.5px] text-muted-foreground text-center py-1">Καμία κοινοποίηση</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <AICategoryPanel
          caseId={currentCase.id} promptKey="case_summary" titleEl="Σύνοψη Υπόθεσης (AI)"
          outputs={aiOutputs} onGenerated={addAIOutput}
        />
      </div>

      {/* File preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="preview-modal">
          <div className="surface-card w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-border/60 shrink-0">
              <div className="min-w-0">
                <h3 className="font-heading font-semibold text-[14px] truncate">{preview.filename}</h3>
                <div className="text-[11px] text-muted-foreground font-mono">{preview.content_type} · {(preview.size_bytes/1024).toFixed(1)} KB</div>
              </div>
              <div className="flex gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.id}/documents/${preview.id}/download`}
                  target="_blank" rel="noreferrer" data-testid="preview-download"
                  className="px-3 py-1.5 rounded-md border border-border/80 text-[12px] hover:border-[hsl(var(--gold)/0.4)]">
                  <Download className="size-3.5 inline mr-1"/>Download
                </a>
                <button onClick={() => setPreview(null)} className="px-3 py-1.5 rounded-md border border-border/80 text-[12px]" data-testid="preview-close">Close</button>
              </div>
            </div>
            <div className="flex-1 bg-muted/10 overflow-hidden">
              {(() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${currentCase.id}/documents/${preview.id}/preview`;
                const ct = preview.content_type || "";
                if (ct.startsWith("image/")) {
                  return <img src={url} alt={preview.filename} className="w-full h-full object-contain"/>;
                }
                if (ct === "application/pdf" || preview.filename.toLowerCase().endsWith(".pdf")) {
                  return <iframe src={url} title={preview.filename} className="w-full h-full" style={{ border: 0 }}/>;
                }
                if (ct.startsWith("text/") || /\.(txt|md|csv|log)$/i.test(preview.filename)) {
                  return <iframe src={url} title={preview.filename} className="w-full h-full bg-white" style={{ border: 0 }}/>;
                }
                return (
                  <div className="h-full flex items-center justify-center text-center p-8">
                    <div>
                      <FileText className="size-16 mx-auto mb-4 text-muted-foreground" strokeWidth={1.2}/>
                      <p className="text-[14px] mb-2">Δεν υπάρχει διαθέσιμη προεπισκόπηση για αυτόν τον τύπο αρχείου.</p>
                      <p className="text-[12px] text-muted-foreground">Κατεβάστε το αρχείο για προβολή.</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
